import { useCallback, useContext } from 'react';
import { V3AppsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';

const useGetContentAppLink = () => {
  const { availableProductApps, allProducts } = useContext(V3AppsContext);

  const buildItemPath = useCallback(
    (app: V3.App, itemKey: string) => {
      const product = allProducts.find((p) => p.appIds.includes(app.id));
      // If no selected product, treat as workspace-style navigation
      if (!product) {
        return `/workspaces/${app.urlSlug}/${itemKey}`;
      }

      // Determine if the product has multiple sub-apps for this user
      const subAppsThisUserHasAccessTo = availableProductApps.filter((a) =>
        product.appIds.includes(a.id),
      );

      if (subAppsThisUserHasAccessTo.length <= 1) {
        return `/apps/${product.urlSlug}/${itemKey}`;
      }

      return `/apps/${product.urlSlug}/${app.urlSlug}/${itemKey}`;
    },
    [allProducts, availableProductApps],
  );

  const getContentAppLink = useCallback(
    ({ app, itemId }: { app: V3.App; itemId: string }): string | undefined => {
      if (!app?.navigationSettings?.sections) return undefined;

      // Flatten all nav items
      const allItems: V3.Navigation.Item[] =
        app.navigationSettings.sections.flatMap((s) => s.items);

      const found = allItems.find((it) => {
        if (it.type === 'Report') {
          return it.reportId === itemId;
        }
        if (it.type === 'Dashboard') {
          return it.dashboardId === itemId;
        }
        if (it.type === 'Scorecard') {
          return it.scorecardId === itemId;
        }
        return false;
      });

      if (!found) return undefined;

      return buildItemPath(app, found.key);
    },
    [buildItemPath],
  );

  return getContentAppLink;
};

export default useGetContentAppLink;
